import {
  cleanCurrency,
  isValidCurrency
} from "@toorak/tc-common-util-sdk/dist/errorCountService/fieldMissingCount";
import { isLATUser } from "@toorak/tc-common-fe-sdk";
import { sanitizeCurrency } from "../../../utils/formatChecks";
import { valuationDropDownValues } from "./Constants";
import { LoanStatusEnum } from "@toorak/tc-common-util-sdk";

const getIsRequiredForAppraisalArvValue = (currentData: any) => {
  const { result, appraisalAsIsValue, index } = currentData || {};
  const { loanDetails, editedValues, loanResult, loanCharacterisations } =
    result || {};
  const loanId = Object.keys(loanResult || {})[0];
  const isMultiPropertyLoan = loanDetails?.propertyDetails?.length > 1;

  let returnValue = index === 0;  

  let loanCharacterisation: string = loanResult?.[loanId]?.loanCharacterisations?.stabilizedFlag;

  const isLoanRefinance =
    loanDetails?.bridgeLoanInformation?.payload?.loanPurpose === "Refinance";

  const isLoanPurposePurchase =
    ["Delayed Purchase", "Purchase"].includes(loanDetails?.bridgeLoanInformation?.payload?.loanPurpose);

  const isStabilizedLoan = loanCharacterisation === "Stabilized";
  const currentValuationsEditedValues = editedValues?.valuations?.[index]
    ? editedValues?.valuations[index]
    : {};

  if (loanDetails?.loanState !== LoanStatusEnum.DataEntry) {
    /* Get values from either the edited values or the original value from store */
    const tempTotalBudget =
      editedValues?.loanEconomics?.totalBudgetAmount ??
      loanDetails?.bridgeLoanEconomics?.payload?.budgetAmount;
    let tempAsIsValue =
      currentValuationsEditedValues?.appraisalAsIsValue ??
      appraisalAsIsValue ??
      loanDetails?.propertyDetails?.[loanDetails?.activePropertyId - 1]
        ?.valuations?.[index]?.payload?.appraisalAsIsValue;
    let tempPurchasePrice =
      editedValues?.propertyDetails?.[index]?.propertyEconomics
        ?.purchasePrice ??
      loanDetails?.propertyDetails?.[loanDetails?.activePropertyId - 1]
        ?.propertyEconomics?.payload?.purchasePrice;

        /*
          If multiproperty loan then the asIs and purchase price should be the aggregated value
        */
      if (isMultiPropertyLoan) {
        let tempAggregatedAsIsValue = 0;
        let tempAggregatedPurchasePrice = 0;

        loanDetails?.propertyDetails?.forEach((prop: any, propIndex: number) => {
          const purchasePriceValue =
            editedValues?.propertyDetails?.[propIndex]?.propertyEconomics
              ?.purchasePrice ||
            prop?.propertyEconomics?.payload?.purchasePrice ||
            "0";
          const asIsValue =
            currentValuationsEditedValues?.appraisalAsIsValue ||
            prop?.valuations?.[index]?.payload?.appraisalAsIsValue ||
            "0";

          tempAggregatedPurchasePrice += purchasePriceValue ? parseFloat(purchasePriceValue?.toString()?.replace(/[^0-9.-]/g, "")) : 0;
          tempAggregatedAsIsValue += asIsValue ? parseFloat(asIsValue?.toString()?.replace(/[^0-9.-]/g, "")) : 0;
        })
        tempAsIsValue = tempAggregatedAsIsValue.toString();
        tempPurchasePrice = tempAggregatedPurchasePrice.toString();
      }
    /* Final data for the checks */
    const totalBudget = parseFloat(
      tempTotalBudget ? typeof tempTotalBudget === "string"
        ? tempTotalBudget.replace(/[^0-9.-]/g, "")
        : tempTotalBudget || 0 : 0
    );
    const asIsValue = parseFloat(
      tempAsIsValue ? typeof tempAsIsValue === "string"
        ? tempAsIsValue?.replace(/[^0-9.-]/g, "")
        : tempAsIsValue || 0 : 0
    );
    const purchasePrice = parseFloat(
      tempPurchasePrice ? typeof tempPurchasePrice === "string"
        ? tempPurchasePrice.replace(/[^0-9.-]/g, "")
        : tempPurchasePrice || 0 : 0
    );

    if (isLoanPurposePurchase) {
      const minValue = Math.min(asIsValue, purchasePrice);
      returnValue = totalBudget <= 0.05 * minValue ? false : true;
    } else if (isLoanRefinance) {
      returnValue = totalBudget <= 0.05 * asIsValue ? false : true;
    } else if (isStabilizedLoan) {
      returnValue = false;
    }
  } else if (isStabilizedLoan) {
    returnValue = false;
  }
  return returnValue;
};

const valuationSource = {
  fieldName: "appraisalType",
  fieldLabel: "Valuation Source",
  required: () => false,
  dropDownData: valuationDropDownValues,
  fieldType: (editManually?: boolean) => "dropDown",
  colSpan: 4,
  isVisible: (data: any) => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  errorMsg: "Please select a value"
};

const valuationValue = {
  fieldName: "appraisalAsIsValue",
  fieldLabel: "As Is Value",
  required: () => true,
  fieldType: (editManually?: boolean) => "currency",
  colSpan: 4,
  isVisible: (data: any) => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  errorMsg: "This field is required"
};

const valuationDate = {
  fieldName: "appraisalDate",
  fieldLabel: "Valuation Date",
  required: () => true,
  fieldType: (editManually?: boolean) => "date",
  colSpan: 4,
  isVisible: (data: any) => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  errorMsg: "This field is required"
};

const originalRepairedAppraisalValue = {
  fieldName: "appraisalArvValue",
  eidtedLabel: "originalRepairedAppraisalValueEdited",
  fieldLabel: "As Repaired Value (ARV) ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: (currentObj: any) => getIsRequiredForAppraisalArvValue(currentObj),
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  commentField: "appraisalArvValue",
  errorMsg: "This field is required"
};

const arvValuationDate = {
  fieldName: "arvValuationDate",
  eidtedLabel: "arvValuationDate",
  fieldLabel: "ARV Valuation Date",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "date",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  commentField: "arvValuationDate",
  errorMsg: "This field is required"
};

const hcValueMedian = {
  fieldName: "hcValueMedian",
  eidtedLabel: "hcValueMedian",
  fieldLabel: "House Canary Value Median",
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  colSpan: 4,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) => true,
  errorMsg: "This field is required"
};

const originalAsIsAppraisalValue = {
  fieldName: "originalAppraisalValue",
  backendKey: "originalAsIsAppraisalValue",
  eidtedLabel: "originalAsIsAppraisalValue",
  fieldLabel: 'Original "As Is" Valuation Value ($)',
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) => true,
  commentField: "originalAsIsAppraisalValue",
  errorMsg: "This field is required"
};

//
const originalAsIsAppraisalValueSource = {
  fieldName: "originalAsRepairedAppraisedValueSource",
  backendKey: "originalAsRepairedAppraisedValueSource",
  eidtedLabel: "originalAsRepairedAppraisedValueSource",
  fieldLabel: "As Repaired Value (ARV) Source",
  dropDownData: valuationDropDownValues,
  colSpan: 4,
  required: () => false,
  fieldType: () => "dropDown",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    !(isEditable && isLATUser()),
  commentField: "arvValueSource",
  errorMsg: "Please select a value"
};

export const getPropertyValuationSources = (
  loanStage: any,
  loanType: any,
  loanSizerType: any
) => {
  switch (loanStage) {
    case "fes":
      return [];
    case "pre":
    case "post":
      return loanType === "InvestorDSCR"
        ? []
        : [
            valuationSource,
            valuationValue,
            valuationDate,
            originalRepairedAppraisalValue
          ];
    default:
      return [];
  }
};

export const getPropertyValuationData = (
  loanStage: any,
  loanType: any,
  loanSizerType: any
) => {
  switch (loanStage) {
    case "fes":
      return [];
    case "pre":
    case "post":
      return loanType === "InvestorDSCR"
        ? []
        : [
            {
              ...valuationSource,
              isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
                true,
              required: () => false,
              commentField: "asIsValueSource"
            },
            hcValueMedian,
            {
              ...valuationDate,
              isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
                true,
              required: () => false
            },
            {
              ...originalRepairedAppraisalValue,
              isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
                true,
              required: () => false
            }
          ];
    default:
      return [];
  }
};

export const getPropertyValuationFinalData = (
  loanStage: any,
  loanType: any,
  loanSizerType: any
) => {
  switch (loanStage) {
    case "fes":
      return [];
    case "pre":
    case "post":
      return loanType === "InvestorDSCR"
        ? []
        : [
            {
              ...valuationSource,
              required: () => false,
              commentField: "asIsValueSource",
              isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
                !(isEditable && isLATUser())
            },
            {
              ...originalAsIsAppraisalValue,
              commentField: "asIsValueForCalculation"
            },
            {
              ...valuationDate,
              isViewMode: () => true,
              required: () => false,
              commentField: "valuationDateForCalculation"
            },
            originalAsIsAppraisalValueSource,
            {
              ...originalRepairedAppraisalValue,
              isViewMode: () => true,
              required: () => false,
              commentField: "arvValueForCalculation"
            },
            {
              ...arvValuationDate,
              isViewMode: () => true,
              required: () => false
            }
          ];
    default:
      return [];
  }
};

export const getPropertyValuationDeskReviewFields = (
  loanStage: any,
  loanType: any,
  loanSizerType: any
) => {
  switch (loanStage) {
    case "fes":
      return [];
    case "pre":
    case "post":
      return loanType === "InvestorDSCR"
        ? []
        : [
            {
              ...valuationSource,
              isViewMode: () => true,
              fieldType: () => "string"
            },
            {
              ...valuationValue,
              fieldLabel: "Desk Review Value Median",
              required: () => false
            },
            {
              ...valuationDate,
              fieldLabel: "Desk Review Valuation Date",
              required: () => false
            },
            { ...originalRepairedAppraisalValue, required: () => false }
          ];
    default:
      return [];
  }
};
